import { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Mail, Phone, MapPin, Send, CheckCircle, 
  Building2, User, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPageEN = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message_type: 'general',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, message_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success('Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message_type: 'general',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'Rruga Muçaj, Building 1\n1032, Vorë, Albania'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+355 68 200 1818',
      href: 'tel:+355682001818'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@albmove.al\nsupport@albmove.al',
      href: 'mailto:info@albmove.al'
    },
    {
      icon: Clock,
      title: 'Hours',
      content: '24 hours / 7 days\nAlways open'
    }
  ];

  return (
    <div data-testid="contact-page-en" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 
              data-testid="contact-title"
              className="text-4xl md:text-5xl font-bold font-['Inter_Tight'] tracking-tight mb-4"
            >
              Contact Us
            </h1>
            <p className="text-lg text-blue-100">
              Have questions? We're here to help. Fill out the form or contact us directly.
            </p>
          </div>
        </div>
      </section>

      <div className="container-custom section-padding">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight'] mb-4">
              Contact Information
            </h2>
            <hr className="border-slate-200 mb-6" />
            
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div 
                  key={index}
                  data-testid={`contact-info-${index}`}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">{info.title}</h3>
                    {info.href ? (
                      <a 
                        href={info.href}
                        className="text-slate-500 hover:text-blue-600 transition-colors whitespace-pre-line"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-slate-500 whitespace-pre-line">{info.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-10 p-6 bg-blue-50 rounded-2xl">
              <h3 className="font-semibold text-blue-900 mb-4">Quick Support</h3>
              <p className="text-sm text-blue-700 mb-4">
                For urgent charging issues, call our 24/7 line.
              </p>
              <a 
                href="tel:+355682001818"
                data-testid="emergency-call-button"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors"
              >
                <Phone className="w-4 h-4" />
                +355 68 200 1818
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200/50 shadow-lg">
              {success ? (
                <div 
                  data-testid="success-message"
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight'] mb-3">
                    Thank You!
                  </h2>
                  <p className="text-slate-500 mb-6">
                    Your message was sent successfully. We'll contact you as soon as possible.
                  </p>
                  <Button 
                    onClick={() => setSuccess(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight'] mb-4">
                    Contact Form
                  </h2>
                  <hr className="border-slate-200 mb-6" />
                  
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          data-testid="input-name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Full Name *"
                          required
                          className="h-12 rounded-xl pl-12"
                        />
                      </div>
                      
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="input-email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email *"
                          required
                          className="h-12 rounded-xl pl-12"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="phone"
                          name="phone"
                          data-testid="input-phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone *"
                          required
                          className="h-12 rounded-xl pl-12"
                        />
                      </div>
                      
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="company"
                          name="company"
                          data-testid="input-company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company (optional)"
                          className="h-12 rounded-xl pl-12"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <Select 
                        value={formData.message_type} 
                        onValueChange={handleSelectChange}
                      >
                        <SelectTrigger 
                          data-testid="select-message-type"
                          className="h-12 rounded-xl"
                        >
                          <SelectValue placeholder="Message Type *" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="business">Business Solutions</SelectItem>
                          <SelectItem value="support">Technical Support</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="mb-6">
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="input-message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your message *"
                        required
                        rows={4}
                        className="rounded-xl resize-none"
                      />
                    </div>

                    <Button 
                      type="submit"
                      data-testid="submit-button"
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-12 text-base font-semibold"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <section className="bg-white py-16">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight']">
              Visit Us
            </h2>
            <p className="text-slate-500 mt-2">Our office in Vorë</p>
          </div>
          
          <div 
            data-testid="office-map"
            className="h-80 rounded-3xl overflow-hidden bg-slate-200 relative"
          >
            <iframe 
              title="AlbMove Office Location - Vorë"
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d1497.9!2d19.663242!3d41.389396!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNDHCsDIzJzIxLjgiTiAxOcKwMzknNDcuNyJF!5e0!3m2!1sen!2sal!4v1706000000000!5m2!1sen!2sal"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPageEN;
